// src/pages/tutor/ReviewDiagnosticPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TestHeader from "../../components/Test/TestHeader";
import TestFooter from "../../components/Test/TestFooter";
import QuestionColumn from "../../components/Test/QuestionColumn";
import ContextColumn from "../../components/Test/ContextColumn";
import ColumnDivider from "../../components/Test/ColumnDivider";
import ReviewNavigator from "../../components/Test/ReviewNavigator";
import DesmosCalculatorWindow from "../../components/Test/DesmosCalculatorWindow";
import ReferenceWindow from "../../components/Test/ReferenceWindow";

import useResizableColumns from "../../hooks/useResizableColumns";
import { fetchCompletedDiagnostic } from "../../utils/diagnostics/fetchCompletedDiagnostic";

import "../../styles/testLayout.css";

const normalizeLetter = (v) => {
  if (v == null) return null;
  const s = String(v).trim().toUpperCase();
  return s === "" ? null : s;
};

const ReviewDiagnosticPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { studentId, attemptId } = useParams();

  const [loading, setLoading] = useState(true);
  const [definition, setDefinition] = useState(null);
  const [allQuestions, setAllQuestions] = useState([]);
  const [attempt, setAttempt] = useState(null);

  const [currentIndex, setCurrentIndex] = useState(0);

  const [showCalculator, setShowCalculator] = useState(false);
  const [showReference, setShowReference] = useState(false);

  const { containerRef, leftWidth, rightWidth, startResize } =
    useResizableColumns({
      initial: 50,
      min: 25,
      max: 75,
    });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const sid = state?.studentId ?? studentId;
        const aid = state?.diagnosticAttemptId ?? state?.attemptId ?? attemptId;

        if (!sid || !aid) {
          console.error("[ReviewDiagnosticPage] missing studentId/attemptId");
          navigate("/tutor/students");
          return;
        }

        const result = await fetchCompletedDiagnostic({
          studentId: sid,
          attemptId: aid,
        });

        setDefinition(result?.definition ?? null);
        setAllQuestions(Array.isArray(result?.questions) ? result.questions : []);
        setAttempt(result?.attempt ?? null);
        setCurrentIndex(0);
      } catch (err) {
        console.error("[ReviewDiagnosticPage] failed to load:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, attemptId]);

  const sequence = useMemo(() => {
    const seq = attempt?.sequence;
    return Array.isArray(seq) ? seq : [];
  }, [attempt]);

  // Normalize question shape to what QuestionColumn expects
  const normalizedQuestions = useMemo(() => {
    return (allQuestions || []).map((q) => {
      const id = q?.id ?? q?.questionId ?? q?.uid ?? null;

      const questionText =
        q?.questionText ?? q?.question ?? q?.prompt ?? q?.stem ?? "";

      const choices =
        q?.answerChoices ?? q?.choices ?? q?.options ?? q?.answers ?? [];

      const correctAnswer =
        q?.correctAnswer ?? q?.answer?.correct ?? q?.answerKey ?? null;

      return {
        id,
        questionText,
        choices,
        passage: q?.passage || null,
        context: q?.context || null,
        imageUrl: q?.imageUrl || null,
        correctAnswer,
        _raw: q,
      };
    });
  }, [allQuestions]);

  const questionById = useMemo(() => {
    const map = new Map();
    for (const q of normalizedQuestions) {
      if (q?.id) map.set(q.id, q);
    }
    return map;
  }, [normalizedQuestions]);

  // Build ordered questions aligned with attempt.sequence
  const orderedQuestions = useMemo(() => {
    return sequence.map((qid) => {
      const found = questionById.get(qid);
      if (found) return found;

      return {
        id: qid,
        questionText: `(Missing question doc: ${qid})`,
        choices: [],
        passage: null,
        context: null,
        imageUrl: null,
        correctAnswer: null,
      };
    });
  }, [sequence, questionById]);

  // Answers aligned to sequence
  const answers = useMemo(() => {
    const resp =
      attempt?.responses && typeof attempt.responses === "object"
        ? attempt.responses
        : {};

    return sequence.map((qid) => normalizeLetter(resp?.[qid]?.answer ?? null));
  }, [attempt, sequence]);

  const marked = useMemo(() => sequence.map(() => false), [sequence]);

  // ✅ NEW: correctness aligned to orderedQuestions/answers indices
  const correctness = useMemo(() => {
    return orderedQuestions.map((q, idx) => {
      const studentAns = normalizeLetter(answers[idx]);
      const correctAns = normalizeLetter(q?.correctAnswer);

      // unanswered → null (so we don’t mark it wrong)
      if (studentAns == null) return null;

      // if we don’t know correct answer → null
      if (correctAns == null) return null;

      return studentAns === correctAns;
    });
  }, [orderedQuestions, answers]);

  const totalQuestions = orderedQuestions.length;
  const isReviewPage = currentIndex === totalQuestions;

  const currentQuestion =
    currentIndex >= 0 && currentIndex < totalQuestions
      ? orderedQuestions[currentIndex]
      : null;

  const headerTitle =
    definition?.name ||
    (attempt?.section ? `SAT ${attempt.section} Diagnostic` : "SAT Diagnostic");

  const headerSection = attempt?.section || definition?.section || "";

  const handleExit = () => {
    const sid = state?.studentId ?? studentId;
    if (sid) {
      navigate(`/tutor/students/${sid}`);
      return;
    }
    navigate("/tutor/students");
  };

  if (loading) {
    return <div style={{ padding: 24 }}>Loading diagnostic review…</div>;
  }

  if (!attempt || totalQuestions === 0) {
    return <div style={{ padding: 24 }}>No diagnostic attempt found.</div>;
  }

  return (
    <div
      ref={containerRef}
      className="test-page"
      style={{
        "--left-col": `${leftWidth}%`,
        "--right-col": `${rightWidth}%`,
      }}
    >
      <TestHeader
        title={headerTitle}
        section={headerSection}
        timeRemaining={null}
        onExit={handleExit}
        onToggleCalculator={() => setShowCalculator((p) => !p)}
        onToggleReference={() => setShowReference((p) => !p)}
        isCalculatorOpen={showCalculator}
        isReferenceOpen={showReference}
      />

      {showReference && (
        <ReferenceWindow onClose={() => setShowReference(false)} />
      )}
      {showCalculator && (
        <DesmosCalculatorWindow onClose={() => setShowCalculator(false)} />
      )}

      {isReviewPage ? (
        <div
          className="test-main"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ReviewNavigator
            currentIndex={currentIndex}
            answers={answers}
            marked={marked}
            onJump={setCurrentIndex}
          />
        </div>
      ) : (
        <div className="test-main">
          <ContextColumn
            passage={currentQuestion?.passage}
            context={currentQuestion?.context}
            imageUrl={currentQuestion?.imageUrl}
          />

          <ColumnDivider onMouseDown={startResize} />

          <QuestionColumn
            question={currentQuestion}
            questionIndex={currentIndex}
            totalQuestions={totalQuestions}
            selectedAnswer={answers[currentIndex]}
            isMarked={false}
            isReadOnly={true}
            setAnswer={undefined}
            toggleMark={undefined}
            isDiagnostic={true}
          />
        </div>
      )}

      <TestFooter
        currentIndex={currentIndex}
        totalQuestions={totalQuestions}
        setCurrentIndex={setCurrentIndex}
        answers={answers}
        marked={marked}
        isReviewPage={isReviewPage}
        isReadOnly={true}
        onSubmit={() => {}}
        onExit={handleExit}
        isDiagnostic={false}
        canAdvance={true}
        isReviewMode={true}
        correctness={correctness}
      />
    </div>
  );
};

export default ReviewDiagnosticPage;
